import React, { useState } from "react";
import { Heading, Icon, Modal, styled, Image, breakpoints } from "@sourceful/shared-components";
import { getImageDimensions, getSanityImgUrl } from "@lib/sanity";
import { SanityTechnicalContentItem } from "@sourceful/shared-types";
import {
  IMAGE_DIMENSION_TECH_SPEC_SECTION_IMAGE_WIDTH,
  IMAGE_DIMENSION_TECH_SPEC_SECTION_MOBILE_IMAGE_WIDTH,
} from "@lib/utils/imageDimensions";

const MobileImage = styled("img", {
  width: "100%",

  variants: {
    isMobileImageModal: {
      true: {
        display: "none",
      },
    },
  },
});

const MobileImageButton = styled("button", {
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%",
  background: "rgba(255,255,255,0.5)",
  cursor: "pointer",
  outline: "none",
  border: "none",
  color: "black",
  fontSize: "2em",
  zIndex: "1",
  display: "none",

  "&:hover, &:active": {
    background: "rgba(255, 255, 255, 0.7)",
  },
  "&:focus": {
    border: "2px solid #dedede",
  },
});

const MainImageContainer = styled("div", {
  position: "relative",

  variants: {
    isMobileImageModal: {
      true: {
        display: "block",
        [`${MobileImageButton}`]: {
          display: "block",
        },
        "@sm": {
          [`${MobileImageButton}`]: {
            display: "none",
          },
        },
      },
    },
  },
});

const MobileImageModalContainer = styled("div", {
  height: "97vh",
  overflowY: "auto",
});

interface ResponsiveImageProps {
  item: SanityTechnicalContentItem;
}

const ResponsiveImage = ({ item }: ResponsiveImageProps) => {
  const { image, mobileImage, isMobileImageModal, title, isTitleHidden } = item;
  const [isMobileModalShowing, setIsMobileModalShowing] = useState(false);

  const hasMobileImage = !!mobileImage && !!mobileImage.asset;

  const imageDimensions = getImageDimensions(image.asset._ref);

  return (
    <>
      {!isTitleHidden && (
        <Heading fontSize={4} level={3} css={{ textAlign: "center", marginBottom: "20px" }}>
          {title}
        </Heading>
      )}
      <MainImageContainer isMobileImageModal={!!isMobileImageModal}>
        {!!isMobileImageModal && hasMobileImage && (
          <MobileImageButton onClick={() => setIsMobileModalShowing(true)}>
            <Icon width={100} height={100} name="toggle-zoom-in" />
          </MobileImageButton>
        )}

        <Image
          src={getSanityImgUrl(image.asset._ref, base =>
            base.width(IMAGE_DIMENSION_TECH_SPEC_SECTION_IMAGE_WIDTH)
          )}
          alt={image?.asset?.altText}
          width={imageDimensions?.width.toString() || "100%"}
          height={imageDimensions?.height.toString() || "auto"}
          loading="lazy"
          alternativeSources={
            hasMobileImage && !isMobileImageModal
              ? [
                  {
                    media: `(max-width: ${breakpoints.sm}px)`,
                    srcSet: getSanityImgUrl(mobileImage ? mobileImage.asset._ref : "", base =>
                      base.width(IMAGE_DIMENSION_TECH_SPEC_SECTION_MOBILE_IMAGE_WIDTH)
                    ),
                  },
                ]
              : []
          }
        />
      </MainImageContainer>

      <Modal
        isOpen={isMobileModalShowing}
        id="mobile-image-modal"
        handleClose={() => setIsMobileModalShowing(false)}
      >
        {hasMobileImage && (
          <MobileImageModalContainer>
            <MobileImage
              src={getSanityImgUrl(mobileImage ? mobileImage.asset._ref : "")}
              alt={mobileImage?.asset?.altText}
            />
          </MobileImageModalContainer>
        )}
      </Modal>
    </>
  );
};

export default ResponsiveImage;
